import { Controller } from "@hotwired/stimulus";
import ApexCharts from "apexcharts";

export default class extends Controller {
  static values = {
    data: Array,
    mode: { type: String, default: "cumulative" },
  };

  static targets = ["chart", "modeButton"];

  connect() {
    requestAnimationFrame(() => {
      this.updateModeButtons();
      this.render();
    });
  }

  disconnect() {
    this._chart?.destroy();
  }

  modeValueChanged() {
    this.updateModeButtons();
    if (this._chart) this.render();
  }

  setMode(event) {
    this.modeValue = event.currentTarget.dataset.mode;
  }

  updateModeButtons() {
    this.modeButtonTargets.forEach((btn) => {
      const active = btn.dataset.mode === this.modeValue;
      btn.classList.toggle("bg-base-100", active);
      btn.classList.toggle("shadow-sm", active);
      btn.classList.toggle("text-base-content", active);
      btn.classList.toggle("text-base-content/40", !active);
    });
  }

  render() {
    this._chart?.destroy();

    const raw = this.dataValue;
    if (!raw.length) {
      this.renderEmpty();
      return;
    }

    let series;
    if (this.modeValue === "cumulative") {
      let cum = 0;
      series = raw.map((d) => {
        cum += d.reach;
        return { x: new Date(d.date).getTime(), y: cum };
      });
    } else {
      series = raw.map((d) => ({
        x: new Date(d.date).getTime(),
        y: d.reach,
      }));
    }

    const seriesName =
      this.modeValue === "cumulative" ? "Cumulative Reach" : "Daily Reach";

    this._chart = new ApexCharts(this.chartTarget, {
      chart: {
        type: "area",
        height: 240,
        width: "100%",
        toolbar: { show: false },
        zoom: { enabled: false },
        background: "transparent",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 400,
          animateGradually: { enabled: false },
        },
        events: {
          mounted: (ctx) => this.removeTitles(ctx),
          updated: (ctx) => this.removeTitles(ctx),
        },
      },
      series: [{ name: seriesName, data: series }],
      stroke: { curve: "smooth", width: 2, colors: ["#3b82f6"] },
      fill: {
        type: "gradient",
        gradient: {
          colorStops: [
            { offset: 0, color: "#3b82f6", opacity: 0.15 },
            { offset: 100, color: "#3b82f6", opacity: 0.02 },
          ],
        },
      },
      markers: {
        size: 0,
        hover: { size: 4, sizeOffset: 0 },
        colors: ["#3b82f6"],
        strokeColors: "#3b82f6",
        strokeWidth: 0,
      },
      xaxis: {
        type: "datetime",
        tooltip: { enabled: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { fontSize: "11px" } },
      },
      yaxis: {
        labels: {
          formatter: (v) => this.fmt(v),
          style: { fontSize: "11px" },
          offsetX: -10,
        },
        crosshairs: { show: false },
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const d = series[dataPointIndex];
          if (!d) return "";
          const date = new Date(d.x).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          });
          const label =
            this.modeValue === "cumulative" ? "Total Reach" : "Reach";
          return `
            <div class="bg-base-100 border border-base-300 rounded-md px-2.5 py-2 text-base-content/80 text-xs">
              <div class="font-medium">${date}</div>
              <div class="mt-1 text-[10px]">${label}: <b>${this.fmt(d.y)}</b></div>
            </div>
          `;
        },
      },
      grid: {
        strokeDashArray: 4,
        padding: { left: 4, right: 4, top: 0, bottom: 0 },
      },
      dataLabels: { enabled: false },
    });

    this._chart.render();
  }

  renderEmpty() {
    const N = 12;
    const data = Array.from({ length: N }, (_, i) => ({
      x: i,
      y: Math.max(
        0.05,
        i / (N - 1) +
          (Math.random() - 0.5) * 0.3 * Math.sin(Math.PI * (i / (N - 1))),
      ),
    }));

    this._chart = new ApexCharts(this.chartTarget, {
      chart: {
        type: "area",
        height: 240,
        width: "100%",
        toolbar: { show: false },
        zoom: { enabled: false },
        background: "transparent",
        events: {
          mounted: (ctx) => this.removeTitles(ctx),
          updated: (ctx) => this.removeTitles(ctx),
        },
      },
      series: [{ data }],
      stroke: { curve: "smooth", width: 2, colors: ["#d1d5db"], dashArray: 5 },
      fill: {
        type: "gradient",
        gradient: {
          colorStops: [
            { offset: 0, color: "#d1d5db", opacity: 0.12 },
            { offset: 100, color: "#d1d5db", opacity: 0.02 },
          ],
        },
      },
      markers: { size: 0, hover: { size: 0 } },
      xaxis: {
        type: "numeric",
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { show: false } },
      grid: { strokeDashArray: 4, padding: { left: 4, right: 4 } },
      dataLabels: { enabled: false },
      tooltip: { enabled: false },
    });

    this._chart.render();
  }

  fmt(n) {
    if (n == null) return "—";
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
    if (n >= 1_000) return (n / 1_000).toFixed(1) + "K";
    return n.toLocaleString();
  }

  removeTitles(chartCtx) {
    chartCtx.el.querySelectorAll("svg title").forEach((t) => t.remove());
  }
}
